import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import logoJMD from "../../../assets/img/logo-jmd.png";
import { FaRegCalendarAlt, FaRegClock } from "react-icons/fa";
import parse from "html-react-parser";
import { Link } from "react-router-dom";
import { getCategoryIdByName } from "../../../utils/categoryResolver";

const defaultArtikel = {
	title: "Jogja Madrasah Digital",
	description: "Artikel Jogja Madrasah Digital.",
	date: "2024-08-15 09:58:35",
	image: logoJMD,
	slug: "jogja-madrasah-digital",
};

function ListArtikel() {
	const sectionRef = useRef(null);
	const hasFetchedRef = useRef(false);

	const [artikel, setArtikel] = useState([]);
	const [isTriggered, setIsTriggered] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	/* ===============================
	INTERSECTION OBSERVER
	Trigger sekali saat scroll
	=============================== */
	useEffect(() => {
		let initialLoad = true;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !initialLoad) {
					setIsTriggered(true);
					observer.disconnect();
				}
				initialLoad = false;
			},
			{ threshold: 0.3 }
		);

		if (sectionRef.current) observer.observe(sectionRef.current);
		return () => observer.disconnect();
	}, []);

	/* ===============================
	FETCH DATA
	=============================== */
	useEffect(() => {
		if (!isTriggered || hasFetchedRef.current) return;

		const fetchData = async () => {
			try {
				setIsLoading(true);
				hasFetchedRef.current = true;

				const catId = (await getCategoryIdByName("Artikel")) || 3;
				const response = await axios.get(`/api_w/posts`, {
					params: { category_id: catId },
					headers: { db_name: process.env.REACT_APP_DB_NAME },
				});

				const data = response.data.data?.filter((p) => p.status === 1) || [];

				setArtikel(data.length ? data : [defaultArtikel]);
			} catch (err) {
				toast.error("Gagal memuat artikel");
				setArtikel([defaultArtikel]);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTriggered]);

	/* ===============================
	HELPERS
	=============================== */
	const formatDate = (date) =>
		new Date(date).toLocaleDateString("id-ID", {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
		});

	const formatTime = (date) =>
		new Date(date).toLocaleTimeString("id-ID", {
			hour: "2-digit",
			minute: "2-digit",
		});

	const truncateText = (text, max) =>
		text.length > max ? text.slice(0, max) + "..." : text;

	/* ===============================
	SKELETON
	=============================== */
	const TitleSkeleton = () => (
		<div className="mb-4 space-y-2 animate-pulse">
			<div className="h-5 w-32 bg-gray-300 rounded" />
			<div className="h-1 w-20 bg-green-400 rounded" />
		</div>
	);

	const ArtikelSkeleton = () => (
		<div className="w-full mb-4 animate-pulse">
			<div className="flex flex-wrap rounded-xl shadow-lg p-4">
				<div className="w-full md:w-1/3 h-40 bg-gray-300 rounded-md" />
				<div className="w-full md:w-2/3 space-y-3 mt-4 md:pl-4">
					<div className="h-5 bg-gray-300 rounded w-5/6" />
					<div className="flex gap-4">
						<div className="h-3 bg-gray-300 rounded w-1/4" />
						<div className="h-3 bg-gray-300 rounded w-1/5" />
					</div>
					<div className="h-3 bg-gray-300 rounded w-full" />
					<div className="h-3 bg-gray-300 rounded w-11/12" />
					<div className="h-3 bg-gray-300 rounded w-10/12" />
				</div>
			</div>
		</div>
	);

	return (
		<div ref={sectionRef}>
			{/* TITLE */}
			{isLoading ? (
				<TitleSkeleton />
			) : (
				<h5 className="mb-4 border-b-2 border-green-500 text-[18px] font-bold inline-block pr-2">
					Artikel
				</h5>
			)}

			{/* LIST */}
			<div className="flex flex-wrap">
				{isLoading ? (
					<>
						<ArtikelSkeleton />
						<ArtikelSkeleton />
					</>
				) : (
					artikel.map((item, i) => (
						<div key={i} className="w-full mb-4">
							<div className="flex flex-wrap rounded-xl shadow-lg">
								<div className="w-full md:w-1/3 p-2">
									<img
										src={item.image}
										alt={item.title}
										className="rounded-md"
									/>
								</div>

								<div className="w-full md:w-2/3 p-2">
									<Link to={`/artikel/${item.slug}`}>
										<h5 className="text-[20px] font-bold hover:text-green-700">
											{truncateText(item.title, 120)}
										</h5>
									</Link>

									<div className="mt-2 flex gap-4 text-sm">
										<div className="flex items-center">
											<FaRegCalendarAlt className="mr-2 text-green-500" />
											{formatDate(item.date)}
										</div>
										<div className="flex items-center">
											<FaRegClock className="mr-2 text-green-500" />
											{formatTime(item.date)} WIB
										</div>
									</div>

									<p className="mt-4 text-justify">
										{parse(truncateText(item.description, 500))}
									</p>
								</div>
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);
}

export default ListArtikel;
